import React, { useRef, useState } from 'react';
import { useForkRef } from '@material-ui/core/utils';
import { Typography, type TypographyProps } from '@popmenu/common-ui';
import { classNames } from '~/utils/withStyles';
import { useScreenReaderMode } from '~/utils/screenReaderMode/useScreenReaderMode';
import { ParentIdContext, useAccessibleHeadingLevel, useLevelProviderId } from './useAccessibleHeadingLevel';

const invisibleStyle = { display: 'none' };

/** @internal used by tests */
export let headingRendersCount = 0;
/** @internal used by tests */
export let levelProviderRendersCount = 0;
/** @internal used by tests */
export function resetHeadingRendersCount() {
  headingRendersCount = 0;
}
/** @internal used by tests */
export function resetLevelProviderRendersCount() {
  levelProviderRendersCount = 0;
}

type HeadingVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

interface AHProps extends Omit<TypographyProps, 'ref' | 'component'> {
  /** Class names unrelated to typography. */
  className?: string;
  /**
   * @deprecated `Typography.component` prop is set automatically inside AH. You cannot override it.
   * `AH.component` prop exists for legacy CSS migrations. It doesn't make sense to use it in new components.
   * Use variant to control styles instead.
   */
  component?: HeadingVariant;
  /** Opt out of screen reader mode's auto-focus on load. Prevents this heading from being
   *  selected as the topmost lazy-loaded heading by ScreenReaderModeManager. */
  skipFocusOnLoad?: boolean;
  /** Whether to use `<Typography variant="h*" ... />` or `<h* />`. */
  typography?: boolean;
  /** Determines class name `.pm-h*`. */
  variant: HeadingVariant;
  tabIndex?: number;
}

// AH means Accessible Heading or Automatic Heading.
//
// See https://web.dev/heading-order/
// Instead of hardcoding headings with h1/h2/h3/h4/h5/h6 use this component.
// It computes heading level by counting AHLevelProvider layers.
//
// Example:
//  <AH> This text is h1 </AH>
//  <AHLevelProvider>
//    <AH> This text is h2 </AH>
//    <AHLevelProvider>
//      <AH> This text is h3 </AH>
//    </AHLevelProvider>
//    <AH> This text is h2 </AH>
//  </AHLevelProvider>
//
// For more examples see spec/cypress/component/consumer/shared/AccessibleHeading.cy.tsx
export const AH = React.forwardRef<HTMLHeadingElement, AHProps>((
  {
    children,
    className = '',
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    component,
    skipFocusOnLoad = false,
    typography = false,
    variant,
    tabIndex,
    ...otherProps
  },
  forwardedRef,
) => {
  headingRendersCount += 1;

  const { onNewHeading } = useScreenReaderMode();

  const [shouldFocus, setShouldFocus] = useState(false);
  const tabIndexValue = shouldFocus ? 0 : tabIndex;

  const ref = useRef<HTMLHeadingElement>(null);
  const setRefs = useForkRef(ref, forwardedRef);
  React.useEffect(() => {
    if (ref.current && onNewHeading && !skipFocusOnLoad) {
      onNewHeading(ref.current.getBoundingClientRect().top, () => setShouldFocus(true));
    }
  }, [onNewHeading, skipFocusOnLoad]);

  React.useEffect(() => {
    if (shouldFocus) {
      setTimeout(() => {
        ref.current?.focus();
      }, 0);
    }
  }, [shouldFocus]);

  const level = useAccessibleHeadingLevel(ref);

  let legacyHeadingLevel: number | null = null;
  if (component && component.length === 2) {
    legacyHeadingLevel = component[1] ? parseInt(component[1], 10) : null;
  } else if (variant.length === 2) {
    legacyHeadingLevel = variant[1] ? parseInt(variant[1], 10) : null;
  }

  // The goal is to change heading level without changing styles. pm-h* and pm-AH class names are used for custom CSS conversion.
  const updatedClassName = classNames(className, 'pm-AH', `pm-h${legacyHeadingLevel}`);
  if (typography) {
    return (
      <Typography
        ref={setRefs}
        variant={variant}
        className={updatedClassName}
        tabIndex={tabIndexValue}
        {...otherProps}
        component={`h${level}`}
      >
        {children}
      </Typography>
    );
  }

  const Tag = ({
    1: 'h1',
    2: 'h2',
    3: 'h3',
    4: 'h4',
    5: 'h5',
    6: 'h6',
  } as const)[level] ?? 'h6';
  return (
    <Tag
      {...otherProps}
      ref={setRefs}
      className={updatedClassName}
      tabIndex={tabIndexValue}
    >
      {children}
    </Tag>
  );
});

interface AHLevelProviderProps {
  children: React.ReactNode;
}

export const AHLevelProvider = ({ children }: AHLevelProviderProps) => {
  levelProviderRendersCount += 1;

  const ref = useRef<HTMLDivElement>(null);
  const id = useLevelProviderId(ref);
  return (
    <ParentIdContext.Provider value={id}>
      <wbr style={invisibleStyle} ref={ref} />
      {children}
    </ParentIdContext.Provider>
  );
};
